// User interest vector: a sparse `{tag: weight}` map driving client-side
// personalization (see `rank.ts`).
//
// Storage budget: the serialized vector must stay under 2 KB in IndexedDB
// (BUILD_PLAN.md §6 Phase 4 acceptance criterion). We enforce that on save by
// pruning to the highest-magnitude entries and rounding weights.
//
// Privacy note: the interest vector is local-only preference data, never an
// identifier, and never leaves the device. It is written only after the user
// has begun onboarding (which itself is gated behind consent acceptance in
// `App.tsx`), so a declining user leaves no personalization state behind.

import { getMeta, setMeta } from "../store/db";

/** Sparse tag → weight map. Absent tags are treated as weight 0. */
export type InterestVector = Record<string, number>;

const META_KEY = "interest_vector";

/** Hard cap on the serialized size, per the Phase 4 acceptance criterion. */
export const MAX_SERIALIZED_BYTES = 2048;

/** Keep at most this many tags so the serialized form stays well under
 * `MAX_SERIALIZED_BYTES` even with long tag names. */
export const MAX_TAGS = 48;

/** Decimal places retained per weight — bounds the serialized size and keeps
 * the numbers readable. */
const WEIGHT_PRECISION = 4;

/** Decay factor applied to existing weights on every engagement update.
 * `weight_new = DECAY * weight_old + delta` (BUILD_PLAN.md §5.5 / §6). */
export const DEFAULT_DECAY = 0.9;

/** Build an initial vector from onboarding selections — every chosen tag gets
 * weight 1.0. */
export function fromOnboarding(tags: readonly string[]): InterestVector {
  const v: InterestVector = {};
  for (const t of tags) v[t] = 1.0;
  return v;
}

/** Apply an exponential-decay update for `tags`:
 * `weight_new = decay * weight_old + delta`. Tags not present in `vec` start
 * from 0. Returns a new vector; the input is not mutated. */
export function applyUpdate(
  vec: InterestVector,
  tags: readonly string[],
  delta: number,
  decay: number = DEFAULT_DECAY,
): InterestVector {
  const next: InterestVector = { ...vec };
  for (const t of tags) {
    const old = next[t] ?? 0;
    next[t] = decay * old + delta;
  }
  return next;
}

function round(x: number): number {
  const f = 10 ** WEIGHT_PRECISION;
  return Math.round(x * f) / f;
}

/** Prune + round so the vector serializes under the budget: drop near-zero
 * weights, keep the `MAX_TAGS` highest-magnitude entries, round the rest. */
export function prune(vec: InterestVector): InterestVector {
  const entries = Object.entries(vec)
    .map(([t, w]) => [t, round(w)] as const)
    .filter(([, w]) => Math.abs(w) >= 1e-4)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, MAX_TAGS);
  const out: InterestVector = {};
  for (const [t, w] of entries) out[t] = w;
  return out;
}

export function serialize(vec: InterestVector): string {
  return JSON.stringify(vec);
}

export function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** Prune, then guarantee the serialized form fits `MAX_SERIALIZED_BYTES` by
 * dropping the lowest-magnitude tags until it does. Pure — this is the size
 * contract `saveInterestVector` relies on, exposed for testing. */
export function fitToBudget(vec: InterestVector): InterestVector {
  let fitted = prune(vec);
  while (byteLength(serialize(fitted)) > MAX_SERIALIZED_BYTES) {
    const entries = Object.entries(fitted);
    if (entries.length <= 1) break;
    entries.sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
    fitted = Object.fromEntries(entries.slice(0, entries.length - 1));
  }
  return fitted;
}

/** Load the persisted interest vector, or `null` if none exists yet. */
export async function loadInterestVector(): Promise<InterestVector | null> {
  const raw = await getMeta(META_KEY);
  if (typeof raw !== "string" || raw.length === 0) return null;
  try {
    return JSON.parse(raw) as InterestVector;
  } catch {
    return null;
  }
}

/** Persist the interest vector, guaranteeing the serialized form is within
 * `MAX_SERIALIZED_BYTES`. Returns the pruned vector that was actually stored. */
export async function saveInterestVector(
  vec: InterestVector,
): Promise<InterestVector> {
  const fitted = fitToBudget(vec);
  await setMeta(META_KEY, serialize(fitted));
  return fitted;
}

/** Clear the persisted interest vector (used by "Forget me"). */
export async function clearInterestVector(): Promise<void> {
  await setMeta(META_KEY, "");
}
